/**
 * ENTERPRISE SAAS CACHE REGISTRY & OPTIMISTIC MUTATORS
 */

export const QUERY_KEYS = {
  inventory: (tenantId, companyId, warehouseId = 'ALL', filters = null) => {
    const baseKey = ['inventoryItems', tenantId || 'GLOBAL', companyId || 'GLOBAL', warehouseId];
    if (filters) {
      const stableFilters = JSON.stringify(
        Object.keys(filters).sort().reduce((acc, key) => {
          acc[key] = filters[key];
          return acc;
        }, {})
      );
      return [...baseKey, stableFilters];
    }
    return baseKey;
  }
};
export const getInventoryQueryKey = (tenantId, companyId, warehouseId = 'ALL', filters = null) => 
  QUERY_KEYS.inventory(tenantId, companyId, warehouseId, filters);

/**
 * OPTIMISTIC UI HELPERS
 * These functions perform "Instant" updates to the UI cache.
 */

/**
 * Optimistically updates an item in all matching inventory queries.
 */
export const applyOptimisticUpdate = (queryClient, { tenantId, companyId, itemId, updates }) => {
  const baseKey = ['inventoryItems', tenantId || 'GLOBAL', companyId || 'GLOBAL'];
  
  // We find all queries that start with this base key (e.g., filtered lists)
  const queries = queryClient.getQueryCache().findAll({ queryKey: baseKey, exact: false });
  
  const snapshots = [];

  queries.forEach(query => {
    const queryKey = query.queryKey;
    // Snapshot current data for potential rollback
    snapshots.push([queryKey, queryClient.getQueryData(queryKey)]);

    queryClient.setQueryData(queryKey, (old) => {
      if (!old || !old.data) return old;
      
      const items = old.data.items || old.data.data?.items || [];
      const updatedItems = items.map(item => 
        item.id === itemId ? { ...item, ...updates, isOptimisticProcessing: true } : item
      );

      // Return structure depends on API response shape
      if (old.data.items) return { ...old, data: { ...old.data, items: updatedItems } };
      if (old.data.data?.items) return { ...old, data: { ...old.data, data: { ...old.data.data, items: updatedItems } } };
      return old;
    });
  });

  return snapshots; // Return for rollback in onError
};

/**
 * Optimistically deletes an item from all matching inventory queries.
 */
export const applyOptimisticDeletion = (queryClient, { tenantId, companyId, itemId }) => {
  const baseKey = ['inventoryItems', tenantId || 'GLOBAL', companyId || 'GLOBAL'];
  const queries = queryClient.getQueryCache().findAll({ queryKey: baseKey, exact: false });
  const snapshots = [];

  queries.forEach(query => {
    const queryKey = query.queryKey;
    snapshots.push([queryKey, queryClient.getQueryData(queryKey)]);

    queryClient.setQueryData(queryKey, (old) => {
      if (!old || !old.data) return old;
      const items = old.data.items || old.data.data?.items || [];
      const filteredItems = items.filter(item => item.id !== itemId);
      
      if (old.data.items) return { ...old, data: { ...old.data, items: filteredItems } };
      if (old.data.data?.items) return { ...old, data: { ...old.data, data: { ...old.data.data, items: filteredItems } } };
      return old;
    });
  });

  return snapshots;
};

/**
 * Optimistically updates the status of a record in any query cache.
 * Useful for "Posting", "Approving", or "Cancelling" documents.
 */
export const applyOptimisticStatusUpdate = (queryClient, { queryKeyPrefix, recordId, newStatus }) => {
  const queries = queryClient.getQueryCache().findAll({ queryKey: queryKeyPrefix, exact: false });
  const snapshots = [];

  queries.forEach(query => {
    const queryKey = query.queryKey;
    snapshots.push([queryKey, queryClient.getQueryData(queryKey)]);

    queryClient.setQueryData(queryKey, (old) => {
      if (!old || !old.data) return old;
      
      const records = old.data.data || old.data.invoices || old.data.orders || old.data.items || [];
      const updatedRecords = records.map(rec => 
        rec.id === recordId ? { ...rec, status: newStatus, isOptimisticProcessing: true } : rec
      );

      // Dynamically detect the data key used by the API (invoices, items, data, etc.)
      const key = old.data.invoices ? 'invoices' : (old.data.orders ? 'orders' : (old.data.items ? 'items' : 'data'));
      
      return { ...old, data: { ...old.data, [key]: updatedRecords } };
    });
  });

  return snapshots;
};

/**
 * Optimistically deletes a record from any query cache.
 */
export const applyOptimisticRecordDeletion = (queryClient, { queryKeyPrefix, recordId }) => {
  const queries = queryClient.getQueryCache().findAll({ queryKey: queryKeyPrefix, exact: false });
  const snapshots = [];

  queries.forEach(query => {
    const queryKey = query.queryKey;
    snapshots.push([queryKey, queryClient.getQueryData(queryKey)]);

    queryClient.setQueryData(queryKey, (old) => {
      if (!old || !old.data) return old;
      
      const key = old.data.invoices ? 'invoices' : (old.data.orders ? 'orders' : (old.data.items ? 'items' : 'data'));
      const records = old.data[key] || (old.data.data && Array.isArray(old.data.data) ? old.data.data : []);
      
      const filtered = records.map(rec => 
        rec.id === recordId ? { ...rec, _isDeleting: true, isOptimisticProcessing: true } : rec
      );
      return { ...old, data: { ...old.data, [key]: filtered } };
    });
  });

  return snapshots;
};

/**
 * Invalidates inventory cache with SaaS-level precision.
 */
export const invalidateInventory = (queryClient, { tenantId, companyId, warehouseId } = {}) => {
  if (!queryClient) return;

  // 1. Invalidate all stockList queries (both overall and warehouse-specific)
  queryClient.invalidateQueries({
    queryKey: ['stockList'],
    exact: false,
    refetchType: 'active'
  });

  // 2. Invalidate all warehouses stock & lookup caches
  queryClient.invalidateQueries({
    queryKey: ['warehouses'],
    exact: false,
    refetchType: 'active'
  });

  // 3. Invalidate inventory items queries across all pagination/filter keys
  queryClient.invalidateQueries({
    queryKey: ['inventoryItems'],
    exact: false,
    refetchType: 'active'
  });

  // 4. Targeted prefix invalidation if tenant/company specified
  if (tenantId || companyId) {
    const invalidationKey = ['inventoryItems', tenantId || 'GLOBAL', companyId || 'GLOBAL'];
    if (warehouseId) invalidationKey.push(warehouseId);
    queryClient.invalidateQueries({
      queryKey: invalidationKey,
      exact: false,
      refetchType: 'active'
    });
  }
};
